import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Airplane, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 hover:bg-amber-100",
  approved: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  rejected: "bg-red-100 text-red-700 hover:bg-red-100",
};

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function Leave() {
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ leave_type: "Casual", start_date: todayISO(), end_date: todayISO(), reason: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/leaves"); setLeaves(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setForm({ ...form, reason: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Time Off</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight gradient-text mt-1">Leave Tracking</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-md border-border lg:col-span-1 h-fit">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2 mb-5"><Plus size={20} /> New Request</h3>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-medium">Type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Casual", "Sick", "Earned", "Maternity/Paternity", "Other"].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">From</Label>
                <Input data-testid="leave-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-medium">To</Label>
                <Input data-testid="leave-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Reason</Label>
              <Textarea data-testid="leave-reason-input" rows={4} value={form.reason} required
                onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 resize-none" placeholder="Reason for leave…" />
            </div>
            <Button type="submit" data-testid="submit-leave-btn" disabled={saving}
              className="w-full rounded-md bg-primary hover:bg-primary/90 transition-transform duration-200 hover:-translate-y-[1px]">
              <Airplane size={18} className="mr-1.5" /> {saving ? "Submitting…" : "Request Leave"}
            </Button>
          </form>
        </Card>

        <Card className="rounded-md border-border overflow-hidden lg:col-span-2 h-fit">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="font-medium">Type</TableHead>
                <TableHead className="font-medium">Dates</TableHead>
                <TableHead className="font-medium">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaves.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No leave requests.</TableCell></TableRow>
              )}
              {leaves.map((l) => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                  <TableCell className="font-medium">{l.leave_type}<div className="text-xs text-muted-foreground truncate max-w-[200px]">{l.reason}</div></TableCell>
                  <TableCell className="font-mono-num text-xs whitespace-nowrap">{l.start_date} → {l.end_date}</TableCell>
                  <TableCell><Badge className={`capitalize ${STATUS_COLORS[l.status]}`}>{l.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
